import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { CheckCircle, XCircle, PackageIcon, Hourglass } from "@phosphor-icons/react";
import { fmtDateTime } from "@/lib/dates";

// Little helper — renders one line's part chip.
function PartChip({ p, tone }) {
  const cls = tone === "faulty"
    ? "bg-red-50 text-red-900 border-red-300"
    : p.status === "used"
      ? "bg-neutral-100 text-neutral-800 border-neutral-300"
      : "bg-emerald-50 text-emerald-900 border-emerald-300";
  return (
    <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[11px] ${cls}`}>
      <span className="font-medium">{p.name}</span>
      <span className="font-mono">× {p.quantity}</span>
      {tone !== "faulty" && (
        <span className="text-[9px] font-mono uppercase tracking-wider opacity-70">{p.status}</span>
      )}
      {tone === "faulty" && p.notes && (
        <span className="text-[10px] italic opacity-90">· {p.notes}</span>
      )}
    </span>
  );
}

export default function ReturnApprovalsPage() {
  const { user, can } = useAuth();
  const canApprove = user?.role === "supervisor" || can("approve_return");
  const canFinalize = user?.role === "supervisor" || can("finalize_return");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending_approval");     // pending_approval | approved
  const [rejecting, setRejecting] = useState(null);       // record for reject dialog
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/records/return-approvals");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter((r) => r.return_stage === tab), [rows, tab]);
  const counts = useMemo(() => ({
    pending_approval: rows.filter((r) => r.return_stage === "pending_approval").length,
    approved: rows.filter((r) => r.return_stage === "approved").length,
  }), [rows]);

  const approve = async (r) => {
    setBusy(`approve:${r.id}`);
    try {
      await api.post(`/records/${r.id}/return-request/approve`, {});
      toast.success(`Verified — awaiting Storekeeper confirmation`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const finalize = async (r) => {
    setBusy(`finalize:${r.id}`);
    try {
      const { data } = await api.post(`/records/${r.id}/return-finalize`, {});
      toast.success(
        data?.status === "returned"
          ? `Finalized ${data.return_receipt_no} — record closed`
          : `Partial finalize ${data?.return_receipt_no} — record still has outstanding parts`
      );
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-neutral-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10 no-print">
        <div className="px-4 py-4 md:px-10 md:py-6 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow">Three-stage flow</div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight leading-none mt-2">Return Approvals</h1>
            <p className="text-sm text-neutral-500 mt-2 max-w-xl hidden md:block">
              Admin/Supervisor <b>verify</b> the return (grey → yellow). Storekeeper then <b>confirms physical receipt</b> to move stock and close the IV (yellow → green).
            </p>
          </div>
        </div>
        <div className="px-4 md:px-10 pb-3 flex gap-2">
          {["pending_approval", "approved"].map((k) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`ra-tab-${k}`}
              className={`h-9 px-4 font-mono text-[11px] uppercase tracking-wider border ${
                tab === k ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-700 border-neutral-300"
              }`}
            >
              {k === "pending_approval" ? "Awaiting Verification" : "Ready to Confirm"} · {counts[k]}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 md:p-10 space-y-3">
        {loading && <div className="text-xs text-neutral-400 border border-neutral-200 bg-white p-6 text-center">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-xs text-neutral-400 border border-neutral-200 bg-white p-10 text-center">
            {tab === "pending_approval" ? "Nothing awaiting approval." : "No approved returns waiting for finalization."}
          </div>
        )}
        {filtered.map((r) => {
          const source = r.return_stage === "approved" ? r.approved_return : r.pending_return;
          const returnedList = source?.parts_returned || [];
          const faultyList = source?.faulty_parts || [];
          return (
            <div key={r.id} className="border border-neutral-200 bg-white p-4 md:p-5 space-y-3" data-testid={`ra-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <PackageIcon size={16} weight="duotone" />
                    <span className="font-mono text-xs font-semibold">{r.receipt_no}</span>
                    <span className="text-neutral-400">·</span>
                    <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider ${
                      r.return_stage === "pending_approval" ? "bg-neutral-200 text-neutral-800" : "bg-amber-100 text-amber-900"
                    }`}>
                      {r.return_stage === "pending_approval" ? "Awaiting Verification" : "Ready to Confirm"}
                    </span>
                  </div>
                  <div className="text-sm font-medium mt-1">{r.engineer_name}</div>
                  <div className="text-[11px] text-neutral-500 font-mono">
                    {r.region}{r.customer_name ? ` · ${r.customer_name}` : ""}{r.work_order_id ? ` · WO ${r.work_order_id}` : ""}
                  </div>
                </div>
                <div className="text-right text-[10px] text-neutral-500 font-mono leading-tight">
                  {source?.submitted_at && (
                    <div>Submitted {fmtDateTime(source.submitted_at)}<br/>by {source.submitted_by_name}</div>
                  )}
                  {source?.approved_at && (
                    <div className="mt-1">Approved {fmtDateTime(source.approved_at)}<br/>by {source.approved_by_name}</div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="label-eyebrow mb-1">Parts returned</div>
                  {returnedList.length === 0
                    ? <div className="text-xs text-neutral-400">None</div>
                    : <div className="flex flex-wrap gap-1.5">
                        {returnedList.map((p, i) => <PartChip key={i} p={p} />)}
                      </div>}
                </div>
                <div>
                  <div className="label-eyebrow mb-1">Faulty parts</div>
                  {faultyList.length === 0
                    ? <div className="text-xs text-neutral-400">None</div>
                    : <div className="flex flex-wrap gap-1.5">
                        {faultyList.map((p, i) => <PartChip key={i} p={p} tone="faulty" />)}
                      </div>}
                </div>
              </div>

              {source?.return_notes && (
                <div className="text-[11px] text-neutral-700 bg-neutral-50 border border-neutral-200 px-3 py-2">
                  <span className="label-eyebrow mr-2">Notes</span>{source.return_notes}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-neutral-100">
                {r.return_stage === "pending_approval" && canApprove && (
                  <>
                    <Button
                      onClick={() => approve(r)}
                      disabled={busy === `approve:${r.id}`}
                      data-testid={`ra-approve-${r.id}`}
                      className="h-10 rounded-none bg-emerald-700 text-white hover:bg-emerald-800"
                    >
                      <CheckCircle size={14} weight="bold" className="mr-2" />
                      {busy === `approve:${r.id}` ? "Verifying…" : "Verify"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setRejecting(r)}
                      data-testid={`ra-reject-${r.id}`}
                      className="h-10 rounded-none border-red-500 text-red-700 hover:bg-red-600 hover:text-white"
                    >
                      <XCircle size={14} weight="bold" className="mr-2" /> Reject
                    </Button>
                  </>
                )}
                {r.return_stage === "approved" && canFinalize && (
                  <Button
                    onClick={() => finalize(r)}
                    disabled={busy === `finalize:${r.id}`}
                    data-testid={`ra-finalize-${r.id}`}
                    className="h-10 rounded-none bg-neutral-900 text-white hover:bg-neutral-800"
                  >
                    <CheckCircle size={14} weight="bold" className="mr-2" />
                    {busy === `finalize:${r.id}` ? "Confirming…" : "Confirm Physical Receipt"}
                  </Button>
                )}
                {r.return_stage === "pending_approval" && !canApprove && (
                  <div className="text-[11px] text-neutral-500 italic flex items-center gap-1">
                    <Hourglass size={12} /> Waiting for Admin/Supervisor/Manager to verify
                  </div>
                )}
                {r.return_stage === "approved" && !canFinalize && (
                  <div className="text-[11px] text-neutral-500 italic flex items-center gap-1">
                    <Hourglass size={12} /> Waiting for Storekeeper to confirm physical receipt
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {rejecting && (
        <RejectDialog
          record={rejecting}
          onClose={() => setRejecting(null)}
          onSaved={() => { setRejecting(null); load(); }}
        />
      )}
    </div>
  );
}

function RejectDialog({ record, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/records/${record.id}/return-request/reject`, { reason });
      toast.success("Return request rejected");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-none border-neutral-900" data-testid="ra-reject-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">Reject Return Request</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-neutral-600">
            Rejecting {record.receipt_no} · {record.engineer_name}. The submitter will need to raise a fresh return request.
          </p>
          <Textarea
            rows={3}
            placeholder="Reason (e.g. wrong qty, needs recount, missing photo)…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="rounded-none border-neutral-400"
            data-testid="ra-reject-reason"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="ra-reject-submit" className="rounded-none bg-red-600 text-white hover:bg-red-700">
            {busy ? "Rejecting…" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
